import { Link } from 'react-router';
import { BlogPostLayout } from '@/components/blog/blog-post-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

const quoteClass = 'border-l-4 border-accent-500 bg-accent-50 rounded-r-2xl p-5 my-8';

export function MechanicsViewHiddenPotholeDamageBlogPost() {
  return (
    <BlogPostLayout
      slug="mechanics-view-hidden-pothole-damage"
      title="The Mechanic's View: The Hidden Damage Drivers Miss After Hitting a Pothole"
      description="A qualified mechanic explains the hidden pothole damage drivers miss, from sidewall bulges to suspension and steering, and how to protect a claim."
      category="Evidence and Documentation"
      readTime="7 min"
      datePublished="2026-05-03"
      dateModified="2026-05-03"
      image={{
        src: '/blog/mechanics-view-hidden-pothole-damage-1200.webp',
        srcSet:
          '/blog/mechanics-view-hidden-pothole-damage-800.webp 800w, /blog/mechanics-view-hidden-pothole-damage-1200.webp 1200w',
        sizes: '(min-width: 1024px) 896px, calc(100vw - 32px)',
        alt: 'Mechanic pointing to a tyre sidewall bulge on a car wheel, showing hidden pothole impact damage drivers can miss.',
        width: 1200,
        height: 630,
        caption:
          'Sidewall bulges are one of the hidden pothole impact faults drivers often miss when they only check the tyre tread.',
      }}
      keywords={[
        'hidden pothole damage',
        'mechanic pothole advice',
        'sidewall bulge',
        'wheel alignment damage',
        'suspension and steering damage',
      ]}
      geoFocus={['England', 'Wales', 'Dudley', 'Stourbridge', 'Halesowen']}
      atAGlance={[
        { label: 'Most missed damage', value: 'Tyre sidewall bulges, wheel alignment, suspension, steering, and underside damage.' },
        { label: 'Why drivers miss it', value: 'The car may still drive normally even when an inner component has been bent or stressed.' },
        { label: 'Safety priority', value: 'Steering faults and tyre sidewall bulges should be checked immediately.' },
        { label: 'Claim protection', value: 'A prompt mechanic report can link hidden damage to impact rather than wear and tear.' },
      ]}
      reviewer={{
        name: 'Elliot Wheeler',
        jobTitle: 'Qualified Mechanic',
        url: 'https://yourlocalmechanic.co.uk/team/elliot-wheeler',
        worksFor: {
          name: 'Your Local Mechanic',
          url: 'https://yourlocalmechanic.co.uk',
        },
        credentialCategory: 'City & Guilds Level 3',
        bio:
          'Elliot Wheeler is a City & Guilds Level 3 qualified mechanic and the founder of Your Local Mechanic, a mobile mechanic service covering Dudley, Stourbridge, Halesowen, Brierley Hill and Kingswinford, with a 5-star rating across 100+ customer reviews. Elliot has more than 15 years of hands-on experience diagnosing and repairing impact and pothole damage.',
      }}
      sources={[
        { name: 'Your Local Mechanic', url: 'https://yourlocalmechanic.co.uk' },
        { name: 'Highways Act 1980, Section 41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'DVSA MOT inspection manual: tyres', url: 'https://www.gov.uk/guidance/mot-inspection-manual-for-private-passenger-and-light-commercial-vehicles/5-axles-wheels-tyres-and-suspension' },
      ]}
      faqs={[
        {
          question: 'What hidden damage can a pothole cause?',
          answer:
            'A pothole impact can cause hidden tyre sidewall bulges, wheel alignment problems, suspension damage, steering component damage, and underside or panel damage, even if the car still drives after the impact.',
        },
        {
          question: 'Why should a mechanic check the car after a hard pothole hit?',
          answer:
            'A mechanic can inspect the tyre sidewalls, suspension, steering, alignment, and underside damage that drivers cannot see from the seat. A written mechanic note can also help link damage to impact for a claim.',
        },
        {
          question: 'Can hidden pothole damage be claimed from the council?',
          answer:
            'Potentially, yes. The key is proving the damage is consistent with the pothole impact and not ordinary wear. Prompt inspection, photos, and repair documentation all help protect the claim.',
        },
      ]}
      relatedPosts={[
        {
          slug: 'spring-pothole-season',
          title: 'Spring Pothole Season',
          description: 'Why winter pothole damage can show up weeks later.',
          readTime: '7 min',
        },
        {
          slug: 'what-evidence-do-you-need-for-pothole-damage-claim',
          title: 'What Evidence Do You Need for a Successful Pothole Damage Claim?',
          description: 'The complete evidence checklist for UK pothole damage claims.',
          readTime: '7 min',
        },
      ]}
    >
      <p>
        Most people judge pothole damage by what they can see. A burst tyre, a scuffed alloy,
        something obvious enough to pull over for. If nothing looks wrong, they drive on and forget
        about it.
      </p>

      <p>
        The trouble, according to the people who actually fix these cars, is that the visible damage
        is rarely the expensive part. We asked Elliot Wheeler, a City & Guilds Level 3 qualified
        mechanic with more than 15 years under cars on a ramp, what drivers miss after hitting a
        pothole, and why the damage they cannot see is the damage that costs them most.
      </p>

      <h2>"The Car Still Drives, So They Think They Got Away With It"</h2>

      <p>
        The single most common mistake, Wheeler says, is assuming that a car which drives away is a
        car that is fine.
      </p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "Nine times out of ten, the person who's done real damage didn't even stop," he says. "They
          felt the bang, the car carried on driving normally, so they assumed no harm done. But a
          pothole hits the wheel and everything attached to it, the tyre, the alloy, the suspension,
          the steering. The tyre might look fine and there's still a bent component further in that
          you'd never spot from the driver's seat."
        </p>
      </blockquote>

      <p>
        That, he explains, is why so much pothole damage goes unclaimed. People only chase the
        council when something is obviously broken. The hidden faults never get connected to the
        pothole at all.
      </p>

      <h2>The Tyre Damage You Cannot See From the Tread</h2>

      <p>Wheeler says the most missed single item is sidewall damage.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "People look at the tread, the bit facing the road, and it looks perfect, so they think the
          tyre's fine," he says. "But the damage from a pothole is usually on the sidewall, the bit
          facing out. You get a little bulge, like a blister on the rubber. I've had people genuinely
          shocked when I tell them the tyre's scrap, because to them it looks brand new."
        </p>
      </blockquote>

      <p>It is not just a cost issue, he stresses. A bulging sidewall is structurally compromised.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "That's an instant MOT failure, and it can blow out at speed. It's one of the ones that
          actually frightens me, because people drive around on them for weeks with no idea."
        </p>
      </blockquote>

      <h2>The Alignment Problem That Creeps Up on You</h2>

      <p>The next hidden cost is wheel alignment, or tracking, knocked out by the impact.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "A hard knock pushes your wheels out of their proper angles," Wheeler explains. "You won't
          feel it straight away. Then a couple of weeks later the car's pulling to one side, or the
          steering wheel's not sitting straight when you're going dead ahead. The giveaway I see most
          is the tyres wearing right down on one edge while the rest of the tread's barely touched."
        </p>
      </blockquote>

      <p>
        Left alone, he says, that one quietly costs you twice: the alignment itself, and a set of
        tyres worn out before their time.
      </p>

      <h2>Suspension and Steering: The Ones That Matter for Safety</h2>

      <p>
        The damage Wheeler takes most seriously is to suspension and steering components, precisely
        because it hides and because it is a safety issue, not a cosmetic one.
      </p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "A big enough impact can bend a suspension arm, crack a coil spring, or wear out the
          bushings and shocks way before they should. The early signs are subtle. A bit of a knock
          over bumps. The ride feeling harder than it used to. The car sitting slightly off-level on
          one corner if you really look. None of it screams 'broken', so people ignore it."
        </p>
      </blockquote>

      <p>And steering, he says, is the line he never wants people to cross without a check.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "If a pothole's affected the steering, that's not something to drive around on and hope.
          That's the one where I'll tell someone straight, get it looked at now, not next month."
        </p>
      </blockquote>

      <h2>The Damage Underneath</h2>

      <p>On lower cars especially, Wheeler sees impact damage people never think to check.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "If the car sits low and the hole's deep, it catches the front bumper, the splitter, the
          underside. I've seen cracked sumps, undertrays hanging half off. Nobody expects bodywork
          damage from a hole in the road, so nobody looks underneath. But it's there."
        </p>
      </blockquote>

      <h2>What He Tells Every Driver to Do</h2>

      <p>Wheeler's advice is consistent and simple, and it doubles as exactly what protects a claim.</p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "If you've taken a proper knock, get it checked even if it feels fine. Two minutes on a
          ramp tells you what's really going on under there, the stuff you can't see from above. And
          do it sooner rather than later, because the longer you leave it, the harder it is to say
          for certain the pothole caused it."
        </p>
      </blockquote>

      <p>
        That last point is the bridge between his workshop and your claim. A prompt professional
        inspection does two jobs at once: it catches the hidden damage before it gets worse or
        dangerous, and it creates the written, dated record that links the damage to the impact,
        which is exactly what a council will challenge.
      </p>

      <blockquote className={quoteClass}>
        <p className="text-brand-950 font-medium">
          "When someone tells me they hit a pothole, I can usually tell impact damage from ordinary
          wear," he adds. "Wear is even and gradual. Impact damage has a pattern. If a mechanic
          writes that down, that's the thing that stops the council waving it away as wear and tear."
        </p>
      </blockquote>

      <p>
        Fixtyer's guides to{' '}
        <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
          documenting vehicle damage
        </Link>{' '}
        and{' '}
        <Link to="/guides/getting-repair-quotes" className={inlineLink}>
          getting the right repair quote
        </Link>{' '}
        explain how to turn a mechanic's inspection into claim-ready evidence.
      </p>

      <h2>Turning a Mechanic's Check Into a Protected Claim</h2>

      <p>Pulling Wheeler's points together, the hidden damage to look for after any significant pothole impact is:</p>

      <ul>
        <li>Sidewall bulges on the tyre, invisible if you only check the tread.</li>
        <li>Wheel alignment knocked out, showing up as pulling or uneven tyre wear weeks later.</li>
        <li>Suspension wear or damage, felt as knocks, a harder ride, or the car sitting off-level.</li>
        <li>Steering issues, which should be checked immediately on safety grounds.</li>
        <li>Underside and panel damage on lower vehicles.</li>
      </ul>

      <p>
        If you hit a pothole hard enough to remember it, the safest and smartest move is a proper
        inspection, even when nothing looks wrong from the driver's seat. It protects you on the
        road, and it protects your right to claim the cost back if the damage turns out to be real.
        If symptoms appeared weeks later, the related article on{' '}
        <Link to="/blog/spring-pothole-season" className={inlineLink}>
          spring pothole season and delayed damage
        </Link>{' '}
        explains how to rebuild that evidence trail.
      </p>

      <h2>The Bottom Line</h2>

      <p>
        The damage that ends up costing drivers the most is rarely the damage they notice. It hides
        in the sidewall, the alignment, the suspension and the steering, surfacing weeks later when
        the pothole is long forgotten. A mechanic sees it every day. The driver almost never does.
        Get the car checked after a hard hit, get any impact damage noted in writing, and you turn an
        invisible problem into a documented, claimable one, before it turns into a bigger bill or a
        safety risk.
      </p>

      <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
        <p className="text-sm text-surface-700 leading-relaxed">
          Fixtyer helps UK drivers prepare and submit their own pothole damage claims, without
          solicitors and without commission, so you keep 100% of any compensation. Fixtyer provides
          documents and guidance only and does not provide legal advice. If you are unsure about your
          individual circumstances, seek independent legal advice.
        </p>
      </div>
    </BlogPostLayout>
  );
}
